import React, { useRef } from "react";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import { DocumentName, UploadImage } from "pages/Admin/TechnicalSanction/style";

import getFileExtension from "utils/getFileExtension";

import { usePutUpdateApplicantFieldsMutation } from "apis/ApplicationRequestDetail";

import pdfImg from "assets/images/pdf.png";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const PanNumberEditField = ({
  t,
  applicationData: {
    applicationId,
    gstNumber,
    panNumber,
    isIndividualApplicantType,
  },
  handleClose,
}) => {
  const ref = useRef();

  const [updatePanNumber, { isLoading }] =
    usePutUpdateApplicantFieldsMutation();

  const formik = useFormik({
    initialValues: {
      panNumber: panNumber ?? "",
      file: "",
    },
    validationSchema: Yup.object({
      panNumber: Yup.string()
        .matches(
          /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
          "Please enter a valid Pan number"
        )
        .test("isGstRequired", "", function (_, context) {
          const [{ value: { panNumber } } = {}] = context.from || [];
          if (!isIndividualApplicantType && !panNumber) {
            return context.createError({
              message: "Pan number is required",
            });
          }
          return true;
        }),
    }),

    onSubmit: async ({ panNumber, file }) => {
      try {
        if ((isIndividualApplicantType && !panNumber) || file) {
          const formData = new FormData();
          formData.append("file", file.file);
          await updatePanNumber({
            query: { panNumber, applicationId, gstNumber },
            payload: formData,
            toastMessage: "Pan number updated successfully",
          });
        } else {
          toast.error("Please upload pan document");
        }
      } catch (error) {}
    },
  });

  const {
    setFieldValue,
    handleSubmit,
    values: { file },
  } = formik;

  const fileChangeHandler = (e) => {
    const file = e.target.files[0];

    const isAllFileValid =
      validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
      file.size < 2097152;

    if (!isAllFileValid) {
      toast.error(
        "Please upload image or pdf file and size should be less than 2 mb"
      );
    } else {
      setFieldValue("file", {
        url: URL.createObjectURL(file),
        name: file.name,
        file: file,
      });
      ref.current.value = "";
    }
  };

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Edit Pan Number"
      maxWidth="sm"
    >
      <Grid container columnSpacing={2} className="mt-3">
        <Grid xs={12}>
          <InputFieldText
            label={t("Pan Number")}
            name="panNumber"
            formik={formik}
            onChange={(e) =>
              setFieldValue("panNumber", e.target.value.toUpperCase())
            }
            placeholder={"Pan Number"}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">{t("Document")} :</Typography>
        </Grid>
        <Grid xs={12} className="ms-3">
          {file ? (
            <div className="w-50 mt-3">
              <div className="image-container">
                <a href={file.url} target="_blank" rel="noreferrer">
                  <Box
                    component="img"
                    className="image d-block m-auto"
                    alt="document image"
                    src={
                      getFileExtension(file.name.toLowerCase()) === "pdf"
                        ? pdfImg
                        : file.url
                    }
                  />
                </a>
                <i
                  className="ri-close-circle-line fs-3 close-btn"
                  onClick={() => {
                    setFieldValue("file", "");
                  }}
                />
                <Tooltip title={file.name}>
                  <DocumentName fontSize={13} variant="body2">
                    {file.name}
                  </DocumentName>
                </Tooltip>
              </div>
            </div>
          ) : (
            <Box component="label" className="m-0 p-0 cursor-pointer">
              <UploadImage isTechnicalFeasibility>
                <AddIcon
                  sx={{
                    color: "black",
                  }}
                />
              </UploadImage>
              <input
                type="file"
                ref={ref}
                accept="image pdf/*"
                onChange={fileChangeHandler}
                multiple
                hidden
              />
            </Box>
          )}
        </Grid>
        <Grid className="d-block ms-auto" mt={1}>
          <Button
            variant="outlined"
            size="small"
            sx={{ height: "40px" }}
            disabled={isLoading}
            onClick={handleSubmit}
          >
            Save {isLoading && <CircularProgress size={20} className="ms-2" />}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

PanNumberEditField.propTypes = {
  t: PropTypes.func,
  applicationData: PropTypes.object,
  handleClose: PropTypes.func,
};

export default withTranslation()(PanNumberEditField);
